import { IdentityApiService } from '@api_patient/identity_api.service';
import { Injectable, Scopes } from '@visiba/instantiation';
import { AuthApiService } from '@api_patient/auth_api.service';
import { UniversalConfig } from '@application/universal_config.service';
import { applicationLocalStorage } from '@application/application_storage_local';
import { signal } from '@preact/signals-react';
import { EventEmitter } from '@visiba/std';
import { Logger } from '@visiba/logger';
import type { FieldOption, IdentityApiModel, SignOutApiModel, UserType } from '@api_patient/generated/clientv2/models';
import { HttpError } from '@application/http_error';
import { I18nAndL10n } from '@application/i18n/i18n_and_l10n';

@Injectable({
	scope: Scopes.Singleton,
})
export class AuthService extends EventEmitter<{
	onSuccessfulAuthentication: User;
	onAuthenticationTermination: User;
	onSuccessfulAuthMethods: Method[];
}> {
	public readonly user = signal<Nullable<User>>(null);
	#logger = new Logger('auth service');
	#authMethods: Method[] | null = null;

	public getUser(): User | null {
		return this.user.peek();
	}

	public getMethods(): Method[] | null {
		return this.#authMethods;
	}

	constructor(
		private readonly identityApiService: IdentityApiService,
		private readonly authApiService: AuthApiService,
		private readonly universalConfig: UniversalConfig,
	) {
		super();
	}

	public isSignedIn(): boolean {
		return this.getUser() != null;
	}

	public async loadIdentity(): AsyncResult<User | null> {
		this.#logger.logInformation('loads identity');

		const result = await this.identityApiService.get();

		if (result.err) {
			this.#logger.logError('could not load user');

			return result /* TODO: This cast can removed when not using Angular */ as unknown as AsyncResult<User, HttpError<unknown>>;
		}

		if (result.ok.data != null) {
			this.user.value = this.createUserObj(result.ok.data);
			this.emit('onSuccessfulAuthentication', this.user.value);
			this.#logger.logInformation('identity loaded');
		}

		return Ok(this.user.value);
	}

	public async terminateIdentity(): AsyncResult<SignOutApiModel> {
		this.#logger.logInformation('signs out user');

		const result = await this.identityApiService.signOut();
		if (result.ok) {
			applicationLocalStorage.removeItem('identitySession');

			const user = this.user.peek();
			if (user != null) {
				this.user.value = null;
				this.emit('onAuthenticationTermination', user);
			} else {
				this.#logger.logError('user was null, is this an error?');
			}

			return Ok(result.ok.data);
		}

		return result /* TODO: This cast can removed when not using Angular */ as unknown as AsyncResult<
			SignOutApiModel,
			HttpError<unknown>
		>;
	}

	public async provideAuthenticationMethods(): AsyncResult<Method[]> {
		const getAuthMethodsResult = await this.authApiService.getAuthMethods(
			this.universalConfig.selectedUnitId,
			this.universalConfig.selectedCountryCode.value,
		);
		if (getAuthMethodsResult.err) {
			return Ok([]);
		}

		this.#authMethods = getAuthMethodsResult.ok.data.map((d) => ({ id: d.MethodId, name: d.Name ?? '-' }));
		this.emit('onSuccessfulAuthMethods', this.#authMethods);

		return Ok(this.#authMethods);
	}

	private createUserObj(response: IdentityApiModel): User {
		const firstName =
			response.FirstName == null && response.LastName == null ? I18nAndL10n.translate('PW.Common.Anonymous') : response.FirstName;
		const commonName = this.getUserCommonName(response.FirstName, response.LastName);

		return {
			id: response.Id,
			licenceHolderId: response.LicenceHolderId,
			userType: response.UserType,
			firstName: firstName ?? '',
			lastName: response.LastName ?? '',
			commonName: commonName,
			email: response.Email,
			nationality: response.Nationality,
			phone: response.Phone,
			nationalIdentityNumber: response.NationalIdentityNumber,
			phoneRequirement: response.PhoneRequirement,
			emailRequirement: response.EmailRequirement,
			nationalIdentityNumberRequirement: response.NationalIdentityNumberRequirement,
		};
	}

	private getUserCommonName(firstName: string | null, lastName: string | null): string {
		if (firstName && lastName) return firstName + ' ' + lastName;
		if (!firstName && lastName) return lastName;
		if (firstName && !lastName) return firstName;

		return I18nAndL10n.translate('PW.Common.Anonymous');
	}
}

export type User = Readonly<{
	id: number;
	licenceHolderId: number;
	userType: UserType;
	firstName: string;
	lastName: string;
	commonName: string;
	phone: string | null;
	email: string | null;
	nationality: string | null;
	nationalIdentityNumber: string | null;
	emailRequirement: FieldOption;
	phoneRequirement: FieldOption;
	nationalIdentityNumberRequirement: FieldOption;
}>;

export type Method = {
	id: string;
	name: string;
};
